/**
 * PF-3b (fieldiq-api#1224) — the AI read of an operator's ILI anomaly list, on
 * the import screen beside the in-browser CSV / XLSX read.
 *
 *   POST /anomalies/import/parse     the AI reads the PDF / XLSX / CSV and PROPOSES rows. Saves nothing.
 *   POST /anomalies/import/confirm   the rows the reviewer kept, with `agent_run_id`
 *   POST /anomalies/import/runs/:id/dismiss   Discard
 *
 * THE ROWS ARE AN AI DRAFT. They are labelled so, every row shows the source
 * reference (and, for a spreadsheet, the source row's text) so the reviewer can
 * check it against the document, and nothing is saved until the reviewer presses
 * Save. Only `ready` rows can be selected or edited: a refused, duplicate,
 * conflicting or repeated row says in words that it will not be saved.
 *
 * A PDF cannot be checked against its source automatically (`sourceVerified:
 * false`) and the screen says so; a list read only in part (`partial`,
 * `failures`) says which parts failed. A 503 / 502 is drawn in the refusal style,
 * never as a success.
 *
 * The confirm's answer goes back to the screen (`onResult`), which draws the
 * same inserted / skipped result as the CSV path.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Sparkles } from 'lucide-react';
import { useDismissPfAnomalyParse, useImportPfAnomalies, useParsePfAnomalyList } from '../../../hooks/usePfDigs';
import { applyParseEdit, PARSE_FIELDS, parseCell, parseErrorSentence, parseRowSelectable, parseStatusLabel } from '../../../lib/pf-anomaly-ai';
import { pfDigRefusal } from '../../../lib/pf-digs';
import type { PfCatalogEntry } from '../../../types/pipeline-facility';
import type { PfAnomalyImportResult, PfAnomalyImportRow, PfAnomalyParseResult } from '../../../types/pf-digs';
import { PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError, PfPanel } from '../PfBits';
import { PfNote, unitLabel } from './DigBits';

export interface PfAnomalyAiReadProps {
  siteId: string;
  /** Optional overrides of what the list states (sent only when entered). */
  vendor: string;
  runDate: string;
  units: { chainage?: string | null; wallThickness?: string | null } | null;
  featureTypes: PfCatalogEntry[];
  /** True while an AI read is in flight or under review (the CSV path waits). */
  onActiveChange: (active: boolean) => void;
  /** Starting an AI read clears the CSV path's rows. */
  onStart: () => void;
  /** The confirm's answer, and each sent row's source reference for the result's Line column. */
  onResult: (result: PfAnomalyImportResult, lines: string[]) => void;
}

export default function PfAnomalyAiRead({ siteId, vendor, runDate, units, featureTypes, onActiveChange, onStart, onResult }: PfAnomalyAiReadProps) {
  const parse = useParsePfAnomalyList();
  const dismiss = useDismissPfAnomalyParse();
  const importer = useImportPfAnomalies();
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [result, setResult] = useState<PfAnomalyParseResult | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [edited, setEdited] = useState<Record<number, PfAnomalyImportRow>>({});
  const [editing, setEditing] = useState<{ index: number; inputs: Record<string, string>; invalid: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const typeLabel = (v: unknown) => {
    if (typeof v !== 'string' || !v) return '—';
    return featureTypes.find((t) => t.value === v)?.label ?? v.replace(/_/g, ' ');
  };
  const refOf = (index: number) => result?.rows.find((r) => r.index === index)?.sourceRef ?? `row ${index + 1}`;
  const rowOf = (index: number): PfAnomalyImportRow | null =>
    edited[index] ?? result?.rows.find((r) => r.index === index)?.row ?? null;

  const clear = () => {
    setResult(null);
    setSelected(new Set());
    setEdited({});
    setEditing(null);
    setFile(null);
    setInputKey((k) => k + 1);
    onActiveChange(false);
  };

  const read = () => {
    if (!file || !siteId) return;
    setError(null);
    setNotice(null);
    onStart();
    onActiveChange(true);
    parse.mutate(
      { file, siteId, iliVendor: vendor.trim() || null, iliRunDate: runDate || null },
      {
        onSuccess: (r) => {
          setResult(r);
          setSelected(new Set(r.rows.filter((x) => parseRowSelectable(x.status)).map((x) => x.index)));
          setEdited({});
          setEditing(null);
          if (!r.rows.length) onActiveChange(false);
        },
        onError: (err) => {
          setError(pfDigRefusal(err, 'The AI could not read the list. Nothing was saved.'));
          onActiveChange(false);
        },
      },
    );
  };

  const toggle = (index: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  const startEdit = (index: number) => {
    const row = rowOf(index);
    if (!row) return;
    const inputs: Record<string, string> = {};
    PARSE_FIELDS.forEach((f) => {
      const v = row[f.key];
      inputs[f.key] = v === null || v === undefined ? '' : String(v);
    });
    setEditing({ index, inputs, invalid: [] });
  };
  const saveEdit = () => {
    if (!editing) return;
    const row = rowOf(editing.index);
    if (!row) return;
    const out = applyParseEdit(row, editing.inputs);
    if (out.invalid.length) return setEditing({ ...editing, invalid: out.invalid });
    setEdited((prev) => ({ ...prev, [editing.index]: out.row }));
    setEditing(null);
  };

  const save = () => {
    if (!result) return;
    const keep = result.rows.filter((r) => parseRowSelectable(r.status) && selected.has(r.index));
    if (!keep.length) return;
    setError(null);
    importer.mutate(
      {
        site_id: siteId,
        ili_vendor: result.iliVendor,
        ili_run_date: result.iliRunDate,
        agent_run_id: result.runId,
        rows: keep.map((r) => edited[r.index] ?? r.row),
      },
      {
        onSuccess: (res) => {
          const lines = keep.map((r) => r.sourceRef ?? `row ${r.index + 1}`);
          clear();
          onResult(res, lines);
        },
        onError: (err) => setError(pfDigRefusal(err, 'The rows could not be saved. Nothing was saved.')),
      },
    );
  };

  const discard = () => {
    if (!result) return;
    setError(null);
    dismiss.mutate(result.runId, {
      onSuccess: () => {
        clear();
        setNotice('Discarded. Nothing from that AI read was saved.');
      },
      onError: (err) => setError(pfDigRefusal(err, 'The AI read could not be discarded.')),
    });
  };

  const selectedCount = result ? result.rows.filter((r) => parseRowSelectable(r.status) && selected.has(r.index)).length : 0;
  const unitOf = (u?: 'chainage' | 'wt') => (u === 'chainage' ? units?.chainage : u === 'wt' ? units?.wallThickness : null);

  return (
    <>
      <PfPanel eyebrow="Or" title="Have the AI read the list" testId="pf-ai-read">
        <div className="space-y-3 px-4 py-4">
          <p className="text-sm text-fieldiq-text">
            For a PDF, or a spreadsheet whose columns are not laid out as above. The AI copies each anomaly row; units, stations and
            checks are done by FieldIQ. You review every row before anything is saved.
          </p>
          <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-end">
            <PfField label="Operator's list (PDF, XLSX or CSV, up to 10 MB)">
              <input
                key={inputKey}
                type="file"
                accept=".pdf,.xlsx,.xls,.csv,application/pdf,text/csv"
                aria-label="Anomaly list for the AI"
                className="block w-full text-sm text-fieldiq-text file:mr-3 file:rounded-fi-control file:border file:border-fieldiq-border file:bg-fieldiq-card file:px-3 file:py-2 file:text-sm file:text-white"
                disabled={parse.isPending || !!result}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </PfField>
            <button
              type="button"
              className={PF_PRIMARY}
              disabled={!file || !siteId || parse.isPending || !!result}
              onClick={read}
              data-testid="pf-ai-read-button"
            >
              <Sparkles className="size-4" aria-hidden="true" />
              {parse.isPending ? 'Reading…' : 'Read with AI'}
            </button>
          </div>
          {!siteId && <p className="text-sm text-fieldiq-muted">Choose the site above first.</p>}
          {parse.isPending && (
            <p className="text-sm text-fieldiq-text" role="status" data-testid="pf-ai-reading">
              Reading {file?.name ?? 'the list'}… a long list can take a minute or two. Nothing is saved.
            </p>
          )}
          {!result && <PfFormError message={error} />}
          {notice && <PfNote testId="pf-ai-discarded">{notice}</PfNote>}
        </div>
      </PfPanel>

      {result && (
        <PfPanel
          eyebrow="Step 2 · AI draft"
          title="Review the AI's rows"
          testId="pf-ai-review"
          footer={`${result.counts.proposed} row${result.counts.proposed === 1 ? '' : 's'} proposed · ${result.counts.ready} ready · ${result.counts.refused} refused · ${result.counts.wouldSkip} would be skipped${
            result.sourceRowCount !== null && result.sourceRowCount !== undefined ? ` · ${result.sourceRowCount} rows in the file` : ''}`}
        >
          <div className="space-y-3 px-4 py-4">
            <p
              data-testid="pf-ai-draft-label"
              className="flex items-start gap-2 rounded-fi-control border border-fi-amber/60 border-l-4 border-l-fi-amber bg-fi-signal-amber/10 px-4 py-3 text-sm font-bold text-fieldiq-text"
            >
              <Sparkles className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              AI draft of {result.fileName ?? 'the list'}. Check every row against the document — nothing is saved until you press Save.
            </p>
            {!result.sourceVerified && (
              <p data-testid="pf-ai-unverified" className="rounded-fi-control border border-fi-amber/60 bg-fi-signal-amber/10 px-4 py-3 text-sm text-fieldiq-text">
                This list is a PDF, so FieldIQ could not check the AI&apos;s values against the file. Each row shows the page it came from — check
                every value against that page before saving.
              </p>
            )}
            {(result.partial || result.failures.length > 0) && (
              <div data-testid="pf-ai-partial" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text" role="alert">
                <p className="font-bold">Only part of the list was read — rows may be missing. Compare the count with the document.</p>
                {result.failures.length > 0 ? (
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {result.failures.map((f) => (
                      <li key={`${f.part}-${f.refs}`}>
                        Part {f.part} ({f.refs}) was not read: {f.error}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1">The AI&apos;s answer was cut off, so rows at the end of the list may not be here.</p>
                )}
              </div>
            )}
            <dl className="grid gap-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">ILI vendor</dt>
                <dd className="text-fieldiq-text" data-testid="pf-ai-vendor">{result.iliVendor || '—'}</dd>
              </div>
              <div>
                <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">ILI run date</dt>
                <dd className="text-fieldiq-text" data-testid="pf-ai-run-date">{result.iliRunDate || '—'}</dd>
              </div>
              <div>
                <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Site</dt>
                <dd className="text-fieldiq-text">{result.siteName || '—'}</dd>
              </div>
            </dl>
            {result.warnings.length > 0 && (
              <ul className="list-disc space-y-1 pl-5 text-xs text-fieldiq-text">
                {result.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
            {result.rows.length === 0 ? (
              <PfNote testId="pf-ai-no-rows">The AI found no anomaly rows in this file. Nothing was saved. Check it is the anomaly list, or enter the rows by hand.</PfNote>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px] text-left text-sm" data-testid="pf-ai-review-table">
                  <thead>
                    <tr className="border-b border-fieldiq-border text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <th className="px-2 py-2">Save</th>
                      <th className="px-2 py-2">Source</th>
                      <th className="px-2 py-2">Check</th>
                      {PARSE_FIELDS.map((f) => (
                        <th key={f.key} className="px-2 py-2">
                          {f.unit ? unitLabel(f.label, unitOf(f.unit)) : f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => {
                      const ok = parseRowSelectable(r.status);
                      const row = edited[r.index] ?? r.row;
                      const isEditing = editing?.index === r.index;
                      return (
                        <React.Fragment key={r.index}>
                          <tr
                            data-testid="pf-ai-row"
                            data-status={r.status}
                            className={`${r.sourceText ? '' : 'border-b border-fieldiq-border/60 '}border-l-4 align-top ${ok ? 'border-l-transparent' : 'border-l-fi-red opacity-80'}`}
                          >
                            <td className="px-2 py-2">
                              <input
                                type="checkbox"
                                aria-label={`Save ${r.row?.feature_id || r.sourceRef || `row ${r.index + 1}`}`}
                                checked={ok && selected.has(r.index)}
                                disabled={!ok || importer.isPending}
                                onChange={() => toggle(r.index)}
                              />
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 font-mono text-xs text-fieldiq-text">{r.sourceRef || '—'}</td>
                            <td className="min-w-[220px] px-2 py-2 text-xs">
                              <p className={ok ? 'text-fieldiq-text' : 'font-bold text-fieldiq-text'} data-testid="pf-ai-row-status">
                                {parseStatusLabel(r, refOf)}
                                {edited[r.index] ? ' · edited' : ''}
                              </p>
                              {r.errors.length > 0 && (
                                <ul className="mt-1 space-y-1 text-fieldiq-text" role="alert">
                                  {r.errors.map((e) => (
                                    <li key={e}>{parseErrorSentence(e, r.row as unknown as Record<string, unknown>)}</li>
                                  ))}
                                </ul>
                              )}
                              {r.warnings.length > 0 && (
                                <ul className="mt-1 list-disc space-y-1 pl-4 text-fieldiq-muted">
                                  {r.warnings.map((w) => (
                                    <li key={w}>{w}</li>
                                  ))}
                                </ul>
                              )}
                              {r.existingId && (
                                <Link className="mt-1 inline-block font-bold text-fi-amber underline" to={`/pipeline-facility/anomalies/${r.existingId}`}>
                                  Open existing
                                </Link>
                              )}
                              {ok && !isEditing && (
                                <button
                                  type="button"
                                  className="mt-1 flex items-center gap-1 font-bold text-fi-amber underline"
                                  onClick={() => startEdit(r.index)}
                                  data-testid="pf-ai-row-edit"
                                  disabled={importer.isPending}
                                >
                                  <Pencil className="size-3" aria-hidden="true" />
                                  Edit
                                </button>
                              )}
                            </td>
                            {PARSE_FIELDS.map((f) => (
                              <td key={f.key} className="px-2 py-2 font-mono text-fieldiq-text">
                                {f.kind === 'type' ? typeLabel(row?.[f.key]) : parseCell(row?.[f.key])}
                              </td>
                            ))}
                          </tr>
                          {r.sourceText && (
                            <tr className="border-b border-fieldiq-border/60" data-testid="pf-ai-source-row">
                              <td />
                              <td colSpan={PARSE_FIELDS.length + 2} className="px-2 pb-2 text-xs text-fieldiq-muted">
                                <span className="font-bold">Source row {r.sourceRef}: </span>
                                <span className="font-mono" data-testid="pf-ai-source-text">{r.sourceText}</span>
                              </td>
                            </tr>
                          )}
                          {isEditing && editing && (
                            <tr className="border-b border-fieldiq-border/60" data-testid="pf-ai-row-editor">
                              <td colSpan={PARSE_FIELDS.length + 3} className="px-2 py-3">
                                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                                  {PARSE_FIELDS.map((f) => (
                                    <PfField key={f.key} label={f.unit ? unitLabel(f.label, unitOf(f.unit)) : f.label}>
                                      {f.kind === 'type' ? (
                                        <select
                                          className={PF_INPUT}
                                          aria-label={`Edit ${f.label}`}
                                          value={editing.inputs[f.key]}
                                          onChange={(e) => setEditing({ ...editing, inputs: { ...editing.inputs, [f.key]: e.target.value } })}
                                        >
                                          {!featureTypes.some((t) => t.value === editing.inputs[f.key]) && (
                                            <option value={editing.inputs[f.key]}>{typeLabel(editing.inputs[f.key])}</option>
                                          )}
                                          {featureTypes.map((t) => (
                                            <option key={t.value} value={t.value}>
                                              {t.label}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          className={PF_INPUT}
                                          aria-label={`Edit ${f.label}`}
                                          inputMode={f.kind === 'number' ? 'decimal' : undefined}
                                          value={editing.inputs[f.key]}
                                          onChange={(e) => setEditing({ ...editing, inputs: { ...editing.inputs, [f.key]: e.target.value } })}
                                        />
                                      )}
                                    </PfField>
                                  ))}
                                </div>
                                <PfFormError message={editing.invalid.length ? `Not a number: ${editing.invalid.join(', ')}. Fix it, or leave it blank.` : null} />
                                <div className="mt-3 flex justify-end gap-2">
                                  <button type="button" className={PF_SECONDARY} onClick={() => setEditing(null)}>
                                    Cancel
                                  </button>
                                  <button type="button" className={PF_PRIMARY} onClick={saveEdit} data-testid="pf-ai-row-edit-apply">
                                    Use these values
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <PfFormError message={error} />
            <div className="flex flex-wrap items-center justify-end gap-2">
              {result.rows.length > 0 && (
                <button type="button" className={PF_SECONDARY} onClick={discard} disabled={dismiss.isPending || importer.isPending} data-testid="pf-ai-discard">
                  {dismiss.isPending ? 'Discarding…' : 'Discard'}
                </button>
              )}
              {result.rows.length === 0 && (
                <button type="button" className={PF_SECONDARY} onClick={clear}>
                  Close
                </button>
              )}
              {result.rows.length > 0 && (
                <button
                  type="button"
                  className={PF_PRIMARY}
                  onClick={save}
                  disabled={!selectedCount || importer.isPending || !!editing}
                  data-testid="pf-ai-save"
                >
                  {importer.isPending ? 'Saving…' : `Save ${selectedCount} selected row${selectedCount === 1 ? '' : 's'}`}
                </button>
              )}
            </div>
            {!!editing && <p className="text-right text-xs text-fieldiq-muted">Finish or cancel the row edit before saving.</p>}
          </div>
        </PfPanel>
      )}
    </>
  );
}
